import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { createDefaultNativeStackNavigator, createWebModalScreenOptions } from 'app/lib/navigation';
import type { Address, NewAddressSource } from 'app/types/address';
import type { FindAllUsersDTO } from 'app/types/user';
import { GroupMembersScreen } from 'app/screens/groups/group-members-screen';

import type { LoanType } from 'app/network/loan';
import { MyClubsScreen } from 'app/screens/profile/my-clubs';
import { ActivitySectorSelectorScreen } from 'app/screens/profile/activity-sector-selector';
import { MyInterestsScreen } from 'app/screens/profile/my-interests';
import { MyNeighbourhoodScreen } from 'app/screens/profile/my-neighbourhood';
import { SuperPartageurDetailsScreen } from 'app/screens/profile/super-partageur-details';
import { AmbassadorScreen } from 'app/screens/profile/ambassador';
import { type Listing, type TransactionMode } from 'app/types/listing';
import { useUISize } from 'app/utils/web-helpers';
import { AccountDeletionScreen } from './account-deletion';
import { ChangePasswordScreen } from './change-password';
import { ContactUsScreen } from './contact-us';
import { MyAddressScreen } from './my-address';
import { MyFinancialImpactScreen } from './my-impact-financial';
import { MyInformationScreen } from './my-information';
import { MyPlanScreen } from './my-plan';
import { MyPreferencesScreen } from './my-preferences';
import { MyRatingsScreen } from './my-ratings';
import { CalendarSettingScreen } from './my-preferences/calendar';
import { LanguageScreen } from './my-preferences/language';
import { NotificationsSettingScreen } from './my-preferences/notifications';
import { PaymentScreen } from './payment';
import { PrivacyScreen } from './privacy';
import ProfileOverviewScreen from './profile-overview';
import { StreakScreen } from './streak';
import { StreakMembersScreen } from './streak/streak-members-screen';
import { SuperPartageurScreen } from './super-partageur';
import { UnavailableScreen } from './unavailable';
import type { ComponentProps } from 'react';

export type Parameters = {
  'account-deletion': undefined;
  'calendar-setting': undefined;
  'contact-us': undefined;
  'my-address': {
    id?: Address['id'];
    address?: Listing['address'] | null;
    goBackTo?: 'new' | 'publish' | 'one-listing' | 'edit-listing';
    listingId?: Listing['id'];
  };
  'change-password': undefined;
  'my-information': undefined;
  'my-plan': { paymentSuccess: boolean } | undefined;
  'my-preferences': undefined;
  'my-ratings': undefined;
  streak: undefined;
  'streak-members': { period: string };
  'super-partageur': undefined;
  'super-partageur-details': undefined;
  ambassador: undefined;
  'my-impact-financial': { type?: LoanType; transactionMode?: TransactionMode } | undefined;
  'notifications-chat-setting': undefined;
  'notifications-need-setting': undefined;
  'notifications-setting': undefined;
  'profile-overview': undefined;
  language: undefined;
  payment: { promoCode?: string } | undefined;
  privacy: undefined;
  'group-members': FindAllUsersDTO;
  'my-clubs': undefined;
  'my-interests': undefined;
  'my-neighbourhood': { addressId?: Address['id']; newAddressSource?: NewAddressSource } | undefined;
  'activity-sectors': undefined;
  unavailable: { startDate: string; endDate: string } | undefined;
};

export { linking } from './navigator.linking';

const Stack = createDefaultNativeStackNavigator<Parameters>();

export const initialRouteName = 'profile-overview';

export function Navigator() {
  const { isSmallUI, isLargeUI } = useUISize();
  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={
        !isSmallUI
          ? {
              cardStyle: { backgroundColor: 'white', flex: 1 },
            }
          : undefined
      }
    >
      <Stack.Screen component={AccountDeletionScreen} name="account-deletion" />
      <Stack.Screen component={ContactUsScreen} name="contact-us" />
      <Stack.Screen component={LanguageScreen} name="language" />
      <Stack.Screen component={MyAddressScreen} name="my-address" />
      <Stack.Screen
        component={PaymentScreen}
        name="payment"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {};
        }}
      />
      <Stack.Screen component={ChangePasswordScreen} name="change-password" />
      <Stack.Screen component={MyInformationScreen} name="my-information" />
      <Stack.Screen component={MyPlanScreen} name="my-plan" />
      <Stack.Screen component={MyPreferencesScreen} name="my-preferences" />
      {isFeatureEnabled(FeatureFlag.rateAfterLoans) && <Stack.Screen component={MyRatingsScreen} name="my-ratings" />}
      <Stack.Screen component={StreakScreen} name="streak" />
      <Stack.Screen
        component={StreakMembersScreen}
        name="streak-members"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {};
        }}
      />
      <Stack.Screen component={SuperPartageurScreen} name="super-partageur" />
      <Stack.Screen component={SuperPartageurDetailsScreen} name="super-partageur-details" />
      <Stack.Screen component={AmbassadorScreen} name="ambassador" />
      <Stack.Screen component={MyFinancialImpactScreen} name="my-impact-financial" />
      <Stack.Screen component={ProfileOverviewScreen} name="profile-overview" />
      <Stack.Screen component={PrivacyScreen} name="privacy" />
      <Stack.Screen component={NotificationsSettingScreen} name="notifications-setting" />
      <Stack.Screen component={CalendarSettingScreen} name="calendar-setting" />
      <Stack.Screen
        component={GroupMembersScreen}
        name="group-members"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {};
        }}
      />
      <Stack.Screen component={MyClubsScreen} name="my-clubs" />
      <Stack.Screen component={ActivitySectorSelectorScreen} name="activity-sectors" />
      <Stack.Screen component={MyInterestsScreen} name="my-interests" />
      <Stack.Screen
        component={MyNeighbourhoodScreen}
        name="my-neighbourhood"
        options={({ navigation, route }) => {
          if (isLargeUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {};
        }}
      />
      <Stack.Screen
        component={UnavailableScreen}
        name="unavailable"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {};
        }}
      />
    </Stack.Navigator>
  );
}
